import React, {useCallback, useEffect, useReducer, useRef} from 'react'
import NotificacionesContext from './NotificacionesContext'
import NotificacionesReducer from './NotificacionesReducer'
import SinWifi from "../../assets/images/sinwifi.jpg"
import ConWifi from "../../assets/images/conwifi.jpg"
import clienteAxios from '../../config/axios'
import {
    LISTAR_NOTIFICACIONES,
    FILTROS_NOTIFICACIONES,
    NUMERO_NOTIFICACIONES,
    WIFI_EN_LINEA
} from '../../types'

const MENSAJE_CONEXION_PERDIDA = "Se perdió la conexión con el servidor. La pantalla se habilitará automáticamente cuando se restablezca la conexión.";
const INTERVALO_VERIFICACION_CONEXION = 15000;
const TIMEOUT_VERIFICACION_CONEXION = 5000;

export default function NotificacionesState (props) {

    const initialState = {
        notificaciones:[],
        filtrosNotificacion:{},
        numeroDeNotificaciones:0,
        WifiActivo:ConWifi,
        servidorDisponible:true,
        mensajeConexion:""
    }

    const [state, dispatch] = useReducer(NotificacionesReducer,initialState)
    const verificandoConexion = useRef(false);

    const esErrorConexion = (error) => {
        return !navigator.onLine || !error.response || error.code === "ERR_NETWORK" || error.code === "ECONNABORTED";
    }

    const actualizarEstadoConexion = useCallback((servidorDisponible) => {
        dispatch({
            type:WIFI_EN_LINEA,
            payload:{
                WifiActivo: servidorDisponible ? ConWifi : SinWifi,
                servidorDisponible,
                mensajeConexion: servidorDisponible ? "" : MENSAJE_CONEXION_PERDIDA
            }
        });
    }, []);

    const verificarConexionServidor = useCallback(async () => {
        if (verificandoConexion.current) {
            return state.servidorDisponible;
        }

        if (!navigator.onLine) {
            actualizarEstadoConexion(false);
            return false;
        }

        verificandoConexion.current = true;

        try {
            await clienteAxios.get("/health", {
                timeout: TIMEOUT_VERIFICACION_CONEXION,
                headers: {
                    "Access-Control-Allow-Origin": "*"
                }
            })

            actualizarEstadoConexion(true);
            return true;
        }catch(error){
            if (esErrorConexion(error)) {
                actualizarEstadoConexion(false);
                return false;
            }

            actualizarEstadoConexion(true);
            return true;
        }finally{
            verificandoConexion.current = false;
        }
    }, [actualizarEstadoConexion, state.servidorDisponible]);

    useEffect(() => {
        const marcarSinConexion = () => actualizarEstadoConexion(false);
        const validarConexion = () => verificarConexionServidor();

        window.addEventListener("offline", marcarSinConexion);
        window.addEventListener("online", validarConexion);

        const intervaloConexion = setInterval(validarConexion, INTERVALO_VERIFICACION_CONEXION);
        validarConexion();

        return () => {
            window.removeEventListener("offline", marcarSinConexion);
            window.removeEventListener("online", validarConexion);
            clearInterval(intervaloConexion);
        }
    }, [actualizarEstadoConexion, verificarConexionServidor]);

    useEffect(() => {
        const interceptorRespuesta = clienteAxios.interceptors.response.use(
            response => response,
            error => {
                if (esErrorConexion(error)) {
                    actualizarEstadoConexion(false);
                }

                return Promise.reject(error);
            }
        );

        return () => {
            clienteAxios.interceptors.response.eject(interceptorRespuesta);
        }
    }, [actualizarEstadoConexion]);

    useEffect(() => {
        const controlarErrorConexionNoCapturado = (event) => {
            if (esErrorConexion(event.reason)) {
                event.preventDefault();
                actualizarEstadoConexion(false);
            }
        };

        window.addEventListener("unhandledrejection", controlarErrorConexionNoCapturado);

        return () => {
            window.removeEventListener("unhandledrejection", controlarErrorConexionNoCapturado);
        }
    }, [actualizarEstadoConexion]);

    const listarNotificaciones = async (filtrosNotificacion) => {
        try
        {
            const res = await clienteAxios.post("/notificaciones", filtrosNotificacion, {
                headers: {
                    "Access-Control-Allow-Origin": "*"
                }
            })
            dispatch({
                type: LISTAR_NOTIFICACIONES,
                payload: res.data
            })
        }catch(error){
            if (esErrorConexion(error)) {
                actualizarEstadoConexion(false);
            }
        }
    }

    const setFiltrosNotificacion = async (filtrosNotificacion) => {
        try
        {
            dispatch({
                type: FILTROS_NOTIFICACIONES,
                payload: filtrosNotificacion
            })
        }catch(error){
            if (esErrorConexion(error)) {
                actualizarEstadoConexion(false);
            }
        }
    }

    const readNotificacion = async (idNotificacion) =>
    {   try
        {
            await clienteAxios.post("/notificaciones/leida", {idNotificacion:idNotificacion},
            {
                headers: {
                    "Access-Control-Allow-Origin": "*"
                }
            })
            obtenerNumeroNotificaciones();
        }catch(error){
            if (esErrorConexion(error)) {
                actualizarEstadoConexion(false);
            }
        }
    }

    const unReadNotificacion = async (idNotificacion) =>
    {   try
        {
            await clienteAxios.post("/notificaciones/noleida", {idNotificacion:idNotificacion},
            {
                headers: {
                    "Access-Control-Allow-Origin": "*"
                }
            })
            obtenerNumeroNotificaciones();
        }catch(error){
            if (esErrorConexion(error)) {
                actualizarEstadoConexion(false);
            }
        }
    }

    const obtenerNumeroNotificaciones = async () => {
        try
        {
            let fecha = global.fechaInicial;

            const res = await clienteAxios.post("/notificaciones/numero",{
                fechaInicial: fecha,
                fechaFinal: new Date().toISOString().slice(0, 10),
            }, {
                headers: {
                    "Access-Control-Allow-Origin": "*"
                }
            })

            dispatch({
                type: NUMERO_NOTIFICACIONES,
                payload: res.data
            })

            actualizarEstadoConexion(true);
            return true;
        }catch(error){
            if (esErrorConexion(error)) {
                actualizarEstadoConexion(false);
                return false;
            }

            actualizarEstadoConexion(true);
            return true;
        }
    }

    return (
        <NotificacionesContext.Provider value={{
            notificaciones: state.notificaciones,
            filtrosNotificacion: state.filtrosNotificacion,
            numeroDeNotificaciones: state.numeroDeNotificaciones,
            WifiActivo:state.WifiActivo,
            servidorDisponible:state.servidorDisponible,
            mensajeConexion:state.mensajeConexion,
            listarNotificaciones,
            setFiltrosNotificacion,
            readNotificacion,
            unReadNotificacion,
            obtenerNumeroNotificaciones,
            verificarConexionServidor
        }}>
            {props.children}
            {!state.servidorDisponible ? (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: 99999,
                        backgroundColor: "rgba(255, 255, 255, 0.92)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "24px",
                        textAlign: "center"
                    }}>
                    <div
                        style={{
                            maxWidth: "460px",
                            backgroundColor: "#ffffff",
                            borderRadius: "8px",
                            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.18)",
                            padding: "30px"
                        }}>
                        <img
                            src={SinWifi}
                            alt="Sin conexión"
                            style={{width: "64px", height: "64px", marginBottom: "16px"}}
                        />
                        <h2 style={{fontSize: "22px", fontWeight: 700, marginBottom: "12px"}}>
                            Sin conexión con el servidor
                        </h2>
                        <p style={{fontSize: "16px", marginBottom: "8px"}}>
                            {state.mensajeConexion}
                        </p>
                        <p style={{fontSize: "14px", color: "#555555", marginBottom: 0}}>
                            Por favor, notifique a su departamento de soporte si el problema continúa.
                        </p>
                    </div>
                </div>
            ) : null}
        </NotificacionesContext.Provider>
    )
}
